import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * Route-level code splitting for the Turbo Pages SPA.
 *
 * This file is the single source of truth for every page's dynamic import.
 * TurboRoutes feeds these thunks to React.lazy() for routing, and the helpers
 * below fire the same thunks ahead of navigation. Dynamic imports are deduped
 * per module, so a preloaded chunk is never downloaded twice — navigating picks
 * up the in-flight or already-cached module.
 *
 * Preloading happens in two layers:
 *   1. Intent: the turbo branch of `Link` (utils/turboSafeInertia.tsx) calls
 *      preloadRoute() on hover/focus/touch. Hover-to-click is typically
 *      200-400ms, enough for a page chunk to arrive before the click lands.
 *   2. Idle backstop: preloadAllRoutesWhenIdle() runs once after the window
 *      `load` event, fetching remaining chunks one at a time during idle
 *      periods so they never compete with first-paint resources.
 */

type PageLoader = () => Promise<{ default: ComponentType }>;

export const routeImports: Record<string, PageLoader> = {
    "/": () => import("@/Pages/Home/CareerPlusHome"),
    "/quiz": () => import("@/Pages/Quiz"),
    "/start": () => import("@/Pages/Start"),
    "/register": () =>
        import("@/Pages/Auth/ClientRegister/ClientRegisterWithGoogle"),
    "/faq": () => import("@/Pages/FAQ"),
    "/contact": () => import("@/Pages/ContactUs"),
    "/privacy": () => import("@/Pages/Legalese/PrivacyPolicy"),
    "/terms": () => import("@/Pages/Legalese/TOS"),
    "/about": () => import("@/Pages/About"),
    "/reviews": () => import("@/Pages/Reviews/Reviews"),
    "/resources/archetypes": () => import("@/Pages/Archetypes"),
};

const RELOAD_FLAG = "turbo_chunk_reload";

/**
 * Wraps a route's import for use as a router component.
 *
 * A deploy replaces every hashed chunk, so a tab that was left open before it
 * requests a file the CDN no longer has. That 404 would otherwise leave the
 * visitor on a dead page, so reload once — guarded by a session flag, since a
 * chunk that is genuinely broken must not put the tab in a reload loop.
 */
export function lazyRoute(path: string): LazyExoticComponent<ComponentType> {
    return lazy(() => {
        const load = routeImports[path];
        if (!load) {
            throw new Error(`No turbo route import registered for "${path}"`);
        }

        return load().then(
            (module) => {
                sessionStorage.removeItem(RELOAD_FLAG);
                return module;
            },
            (error) => {
                if (!sessionStorage.getItem(RELOAD_FLAG)) {
                    sessionStorage.setItem(RELOAD_FLAG, "1");
                    window.location.reload();
                }
                throw error;
            },
        );
    });
}

const importablePaths = Object.keys(routeImports).sort(
    (a, b) => b.length - a.length,
);

function findLoader(path: string): PageLoader | undefined {
    const match = importablePaths.find(
        (candidate) => path === candidate || path.startsWith(`${candidate}/`),
    );
    return match ? routeImports[match] : undefined;
}

/**
 * Fires a route's import ahead of navigation. Safe to call repeatedly, with
 * absolute or relative hrefs, and with paths this SPA does not serve — an
 * unknown path is a no-op.
 */
export function preloadRoute(href: string): void {
    let path: string;
    try {
        path = new URL(href, window.location.origin).pathname;
    } catch {
        return;
    }

    const load = findLoader(path);
    if (!load) {
        return;
    }

    void load().catch(() => {
        // Preloading is opportunistic: if it fails (offline, or a deploy
        // replaced the chunk) the real import on navigation surfaces the error.
    });
}

let hasStartedIdlePreload = false;

/**
 * Fetches every remaining route chunk after the page has fully loaded, one at a
 * time during browser idle periods. Skips entirely on data-saver or 2G.
 */
export function preloadAllRoutesWhenIdle(): void {
    if (hasStartedIdlePreload) {
        return;
    }
    hasStartedIdlePreload = true;

    const { connection } = navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string };
    };
    if (connection?.saveData === true) {
        return;
    }
    if (connection?.effectiveType?.endsWith("2g") === true) {
        return;
    }

    const scheduleIdle = (callback: () => void): void => {
        if (typeof globalThis.requestIdleCallback === "function") {
            globalThis.requestIdleCallback(callback);
        } else {
            globalThis.setTimeout(callback, 1500);
        }
    };

    const queue = Object.values(routeImports);
    const loadNext = async (): Promise<void> => {
        const load = queue.shift();
        if (!load) {
            return;
        }
        try {
            await load();
        } catch {
            // Same reason as preloadRoute: opportunistic, never fatal.
        }
        scheduleIdle(() => void loadNext());
    };

    const start = (): void => scheduleIdle(() => void loadNext());
    if (document.readyState === "complete") {
        start();
    } else {
        window.addEventListener("load", start, { once: true });
    }
}
